import { zodUnionFromArray } from 'utils/zodUnionFromArray'
import { parseEther, parseUnits } from 'viem'
import { z } from 'zod'

export const MAX_LENGTH_SPACE_NAME = 32
export const MAX_LENGTH_SPACE_BIO = 240
export const PLACEHOLDER_MINIMUM_USDC_PRICE = '0.01'

export const clientTownTypeOptions = ['free', 'paid'] as const

const spaceName = z
    .string()
    .min(2, 'Town Name must have at least 2 characters')
    .max(MAX_LENGTH_SPACE_NAME, 'Town Name must be less than 32 characters')
    .refine((val) => !val.match(/^\s+|\s+$/), {
        message: 'Please remove any leading or trailing spaces',
    })
    .transform((val) => val.trim())

export const createTownWithDurationFormSchema = (minMembershipCost: string, isUSDC = false) =>
    z
        .object({
            clientTownType: zodUnionFromArray(clientTownTypeOptions).nullable(),
            spaceName,
            spaceIconUrl: z.coerce.string().nullable(),
            spaceIconFile: z
                .custom<File | null>(
                    (val) => val === null || val instanceof File,
                    'Please upload an image',
                )
                .nullable(),
            membershipCost: z.string(),
            duration: z.number().nullable(),
        })
        .superRefine((data, ctx) => {
            if (!data.spaceName) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['spaceName'],
                    message: 'Please enter a town name.',
                })
            }

            if (data.clientTownType === 'paid' && data.duration === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['duration'],
                    message: 'Please select a duration.',
                })
            }

            if (data.clientTownType === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['clientTownType'],
                    message: 'Please select a town type.',
                })
            }

            if (data.spaceIconFile === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['spaceIconFile'],
                    message: 'Please upload an image.',
                })
            }

            if (isUSDC) {
                try {
                    const priceInSmallestUnit = parseUnits(data.membershipCost, 6)

                    if (data.clientTownType === 'paid') {
                        const minPriceInSmallestUnit = parseUnits(PLACEHOLDER_MINIMUM_USDC_PRICE, 6)
                        if (priceInSmallestUnit < minPriceInSmallestUnit) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: ['membershipCost'],
                                message: `Price must be at least ${PLACEHOLDER_MINIMUM_USDC_PRICE} USDC`,
                            })
                        }
                    }
                } catch (error) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['membershipCost'],
                        message: 'Please enter a valid USDC value.',
                    })
                }
            } else {
                try {
                    const priceInWei = parseEther(data.membershipCost)

                    if (data.clientTownType === 'paid') {
                        if (priceInWei < parseEther(minMembershipCost)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: ['membershipCost'],
                                message: `Price must be at least ${minMembershipCost} ETH`,
                            })
                        }
                    }
                } catch (error) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['membershipCost'],
                        message: 'Please enter a valid ETH value.',
                    })
                }
            }

            // TODO: add duration validation
        })

export type CreateTownWithDurationFormSchema = z.infer<
    ReturnType<typeof createTownWithDurationFormSchema>
>
